import type { Api } from "../data/api";
import { date, moneyWhole, numberLocale } from "../lib/format";
import { t, tr, type Lang } from "../lib/sprache";

/**
 * Tarifstaffel (0099): Module je Tarif, der kleinste Tarif mit einem Modul, Grenzen ab 80 % Belegung und die Meldung
 * „tarif_modul:<schlüssel>“ der Datenbank. Reine Logik und Lader ohne React; Hooks und Bausteine in components/Tarif.tsx.
 *
 * Die Datenbank sperrt Aktionen mit 'tarif_modul:<schlüssel>' (P0001, hint = Name des kleinsten Tarifs mit dem Modul).
 * Die Oberfläche spiegelt das: Seiten bleiben sichtbar, die gesperrte Aktion ist deaktiviert und nennt den Tarif.
 */

/** Alle Module des Katalogs, die ein Tarif enthalten kann (0099: acht neue, dazu Insights und WhatsApp; 0154: Freigabe-Link). */
export const MODUL_KEYS = [
  "module_insights", "module_whatsapp",
  "module_dunning_custom", "module_rights", "module_payouts", "module_accounting_export", "module_pipeline", "module_media_kit",
  "module_bank_sync", "module_sender_domain", "module_brand_review", "module_mail_unbranded",
] as const;
export type ModulKey = (typeof MODUL_KEYS)[number];

/** Grenzen des Tarifs (-1 = unbegrenzt). */
export const GRENZ_KEYS = ["max_talents", "max_seats", "max_contracts_per_month", "storage_gb"] as const;

/**
 * Feste Zuordnung „kleinster Tarif mit dem Modul“ nach dem Katalog von 0099. Rückfall, solange der Katalog (plans,
 * plan_entitlements) nicht geladen ist oder nicht lesbar war; sonst gilt der Katalog wie in app.require_module. Stand 0154
 * (Preismodell 2026): Insights ab Agentur, Freigabe-Link ab Team.
 */
export const MODUL_AB_TARIF: Record<ModulKey, "start" | "team" | "agentur"> = {
  module_insights: "agentur",
  module_brand_review: "team",
  module_dunning_custom: "start", // 0172: Mahnwesen in jedem Tarif
  module_rights: "team",
  module_payouts: "team",
  module_accounting_export: "team",
  module_pipeline: "team",
  module_media_kit: "team",
  module_whatsapp: "agentur",
  module_bank_sync: "agentur",
  module_sender_domain: "agentur",
  module_mail_unbranded: "agentur",   // 0226: Band „Erstellt mit CreatorOffice“ in Mails ausblenden
};
/** Produktnamen der Tarife wie im Katalog; „Agentur“ heißt auf Englisch „Agency“ (Entscheidung 07.10.2026), die übrigen gleich. */
const TARIF_NAME: Record<string, () => string> = { start: () => "Solo", team: () => "Team", agentur: () => t("Agentur", "Agency"), creator: () => "Creator", creator_pro: () => "Creator Pro" };

/** 0349: Tarife der Creator-Konten (zwei Stufen) */
export const CREATOR_PLANS = ["creator", "creator_pro"] as const;
export function isCreatorPlan(key: string | null | undefined): boolean {
  return (CREATOR_PLANS as readonly string[]).includes(key ?? "");
}
/** Anzeigename eines Tarifschlüssels ohne geladenen Katalog (etwa „start“ → „Solo“ auf der Registrierung); unbekannt: groß geschrieben. */
export function tarifName(key: string): string {
  return TARIF_NAME[key]?.() ?? key.charAt(0).toUpperCase() + key.slice(1);
}
/** Anzeigename eines Tarifs aus dem Katalog (plans.name ist deutsch) in der Oberflächensprache: „Agentur“ → „Agency“. */
export function planName(p: { name: string }): string {
  return t(p.name, p.name.replace(/^Agentur\b/, "Agency").replace("(bisheriger Preis)", "(previous price)"));
}

/**
 * Gruppen für Modul-Panel und Tarifvergleich (Abo-Seite). Grundlagen sind die Grenzen, danach die Module.
 * Jeder Schlüssel aus GRENZ_KEYS und MODUL_KEYS steht in genau einer Gruppe.
 */
export const TARIF_GRUPPEN: readonly { key: "grundlagen" | "zusammenarbeit" | "finanzen"; readonly label: string; keys: readonly string[] }[] = [
  { key: "grundlagen", get label() { return t("Grundlagen", "Basics"); }, keys: GRENZ_KEYS },
  {
    key: "zusammenarbeit", get label() { return t("Zusammenarbeit & Wachstum", "Collaboration & growth"); },
    keys: ["module_rights", "module_pipeline", "module_media_kit", "module_brand_review", "module_insights", "module_sender_domain", "module_mail_unbranded"],
  },
  {
    key: "finanzen", get label() { return t("Finanzen & Automatik", "Finance & automation"); },
    keys: ["module_dunning_custom", "module_payouts", "module_accounting_export", "module_bank_sync", "module_whatsapp"],
  },
];

// ── Feste Zahlen außerhalb des Katalogs (K-497 g) ──────────────────────────────────────────────────

/**
 * Enterprise ist kein Tarif im Katalog (0154), sondern ein Gespräch. Diese eine Konstante liefert Preis und Grenzen für alle
 * Stellen der App (Tarifwahl, Tarifkarten, Wechseldialog); die Grenzen der buchbaren Tarife kommen immer aus plan_entitlements.
 */
export const ENTERPRISE = { abCents: 99_000, maxTalents: 150, maxSeats: 15 } as const;
/** Dauer der Testphase, solange platform_defaults.trial_days nicht gelesen ist (Standard der Datenbank, 0037) */
export const TESTTAGE = 14;

/** „ab 990 €“ (ganze Euro, wie auf der Website) */
export function enterprisePreis(): string {
  return moneyWhole(ENTERPRISE.abCents, "EUR");
}
/**
 * Enterprise in einem Satz (K-497 g). Mit den Grenzen des größten buchbaren Tarifs (Agentur, aus plan_entitlements) davor die
 * Frage „Mehr als 60 Creator oder 8 Plätze?“; ohne sie nur der Satz.
 */
export function enterpriseText(groesster?: { talents: number | null | undefined; seats: number | null | undefined } | null): string {
  const frage = groesster && typeof groesster.talents === "number" && groesster.talents > 0 && typeof groesster.seats === "number" && groesster.seats > 0
    ? t(`Mehr als ${groesster.talents} Creator oder ${groesster.seats} Plätze? `, `More than ${groesster.talents} creators or ${groesster.seats} seats? `)
    : "";
  return frage + t(
    `Enterprise ab ${enterprisePreis()} im Monat: bis ${ENTERPRISE.maxTalents} Creator und ${ENTERPRISE.maxSeats} Team-Plätze, mehrere Firmen in einem Verbund, Einrichtung und Datenübernahme durch uns, fester Ansprechpartner, Zahlung per Rechnung möglich.`,
    `Enterprise from ${enterprisePreis()} per month: up to ${ENTERPRISE.maxTalents} creators and ${ENTERPRISE.maxSeats} team seats, several companies in one group, setup and data migration by us, a dedicated contact, payment by invoice possible.`);
}

/**
 * K-494: ein Satz an allen Preisen der App (Abo & Tarif, Tarifwahl vor dem Abschluss, PayPal-Abschluss). Inhaber 16.10.2026:
 * überall derselbe Wortlaut.
 */
export function preisNettoHinweis(): string {
  return t("Alle Preise netto, nur für Unternehmer und selbständig tätige Creator. Die Umsatzsteuer meldest du ggf. selbst (Reverse Charge, in CH/LI als Bezugsteuer).",
    "All prices net, for businesses and self-employed creators only. You report VAT yourself where applicable (reverse charge, in CH/LI as acquisition tax).");
}

// ── Tarifstand der Agentur (org_entitlements, für alle Mitarbeiter lesbar) ──────────────────────────

export interface TarifNutzung {
  talents: number;
  seats: number;
  contracts_this_month: number;
}
export interface TarifStand {
  plan: string | null;
  status: string | null;
  limits: Record<string, number>;
  /** Zählwerte wie die Tarif-Wächter; null, wenn die Datenbank sie nicht liefert */
  usage: TarifNutzung | null;
  /** 0216: Testphase im vollen Umfang (die Grenzen sind die des Tarifs Agentur, der gewählte Tarif gilt danach) */
  fullTrial?: boolean;
}

/**
 * Tarifstand für alle Mitarbeiter (0031 org_entitlements): Tarif, Grenzen und Module, Nutzung. Das sind die wirksamen Grenzen,
 * wie die Datenbank sie prüft (Zusatzpakete und Test im vollen Umfang eingerechnet, K-377).
 */
export async function fetchTarifStand(api: Pick<Api, "read">, orgId: string): Promise<TarifStand> {
  const r = await api.read<{ plan?: string; status?: string; limits?: Record<string, number>; usage?: Partial<TarifNutzung>; full_trial?: boolean } | string>("org_entitlements", { p_org: orgId });
  const v = (typeof r === "string" ? JSON.parse(r) : r) ?? {};
  const u = v.usage;
  return {
    plan: v.plan ?? null,
    status: v.status ?? null,
    limits: v.limits ?? {},
    usage: u ? { talents: Number(u.talents ?? 0), seats: Number(u.seats ?? 0), contracts_this_month: Number(u.contracts_this_month ?? 0) } : null,
    fullTrial: v.full_trial === true,
  };
}

/**
 * Wirksame Grenzen (K-377): Im Test im vollen Umfang (0216) gibt die Datenbank die Grenzen des Tarifs Agentur; die Oberfläche
 * darf dann nicht nach dem gewählten Tarif (plan_entitlements) sperren. Liefert die Grenzen aus org_entitlements, wenn sie
 * lesbar sind, sonst die übergebenen des Tarifs.
 */
export async function wirksameGrenzen(api: Pick<Api, "read">, orgId: string, tarifGrenzen: Record<string, number>): Promise<{ limits: Record<string, number>; fullTrial: boolean }> {
  try {
    const s = await fetchTarifStand(api, orgId);
    if (Object.keys(s.limits).length) return { limits: s.limits, fullTrial: !!s.fullTrial };
  } catch {
    /* ohne Lesbarkeit: Grenzen des Tarifs */
  }
  return { limits: tarifGrenzen, fullTrial: false };
}

/** Datum ohne Jahr: „24.10.“ bzw. „24/10“; andere Formate bleiben vollständig. */
export function kurzDatum(iso: string, timeZone?: string): string {
  const d = date(iso, timeZone);
  const m = /^(\d{2})([./])(\d{2})[./]\d{4}$/.exec(d);
  if (!m) return d;
  return m[2] === "." ? `${m[1]}.${m[3]}.` : `${m[1]}${m[2]}${m[3]}`;
}

/** „5 Creator“, „unbegrenzt viele Creator“ (Grenze des gewählten Tarifs für den Satz zur Testphase) */
export function creatorGrenzeText(n: number | null | undefined): string | null {
  if (n === null || n === undefined) return null;
  return n < 0 ? t("unbegrenzt viele Creator", "unlimited creators") : t(`${n} Creator`, n === 1 ? "1 creator" : `${n} creators`);
}
/** „1 Team-Platz“, „3 Team-Plätze“ */
export function platzGrenzeText(n: number | null | undefined): string | null {
  if (n === null || n === undefined) return null;
  return n < 0 ? t("unbegrenzt viele Team-Plätze", "unlimited team seats") : n === 1 ? t("1 Team-Platz", "1 team seat") : t(`${n} Team-Plätze`, `${n} team seats`);
}
/**
 * Ein Satz zur Testphase im vollen Umfang (K-377): „Im Test frei bis 24.10., danach gilt Solo: 5 Creator“. Tarifname und Grenze
 * echt aus dem gewählten Tarif; ohne Grenze nur „danach gilt Solo“.
 */
export function testFreiText(bis: string, tarif: string, grenze: string | null, timeZone?: string): string {
  const d = kurzDatum(bis, timeZone);
  return grenze
    ? t(`Im Test frei bis ${d}, danach gilt ${tarif}: ${grenze}`, `Free during the trial until ${d}, then ${tarif} applies: ${grenze}`)
    : t(`Im Test frei bis ${d}, danach gilt ${tarif}`, `Free during the trial until ${d}, then ${tarif} applies`);
}

/** Modul im Tarif? Wert ab 1; fehlt der Schlüssel, gilt 0 (wie app.entitlement). */
export function modulAn(limits: Record<string, number>, key: string): boolean {
  return (limits[key] ?? 0) >= 1;
}

/**
 * Ist das Modul für die Oberfläche gesperrt? Wie modulAn, mit einer Ausnahme: Kennt der Katalog der Agentur den Schlüssel gar
 * nicht (Tarif mit Grenzen, aber ohne diesen Eintrag, etwa vor dem Einspielen von 0099), sperrt auch die Datenbank nichts; dann
 * sperrt die Oberfläche ebenfalls nicht. Ohne jeden Tarifeintrag (kein Abo) gilt wie in der Datenbank 0, also gesperrt.
 */
export function modulGesperrt(limits: Record<string, number>, key: string): boolean {
  if (!(key in limits)) return Object.keys(limits).length === 0;
  return !modulAn(limits, key);
}

// ── Katalog: kleinster Tarif mit einem Modul ─────────────────────────────────────────────────────────

export interface TarifKatalog {
  /** 0323: Preise (netto, Cent) für Testende-Hinweise und gesperrte Funktionen; fehlen, wo der Katalog sie nicht liefert */
  plans: { key: string; name: string; active: boolean; sort_order: number; price_month_cents?: number | null; price_year_cents?: number | null }[];
  entitlements: { plan_key: string; key: string; value: number }[];
}
/** Tarifkatalog (plans, plan_entitlements): für alle Mitarbeiter lesbar (0058), nicht für Creator. */
export async function fetchTarifKatalog(api: Pick<Api, "select">): Promise<TarifKatalog> {
  const [plans, entitlements] = await Promise.all([
    api.select<TarifKatalog["plans"][number]>("plans", { columns: "key, name, active, sort_order, price_month_cents, price_year_cents", order: [["sort_order", "asc"]] }),
    api.select<TarifKatalog["entitlements"][number]>("plan_entitlements", { columns: "plan_key, key, value" }),
  ]);
  return { plans, entitlements };
}

/** Zusatzpakete (0154, 0157): ein weiterer Creator bzw. Team-Platz je Monat; für alle Mitarbeiter lesbar wie der Katalog */
export interface Zusatzpaket { key: string; entitlement_key: string; active: boolean; price_month_cents: number; price_year_cents: number }
export async function fetchZusatzpakete(api: Pick<Api, "select">): Promise<Zusatzpaket[]> {
  return api.select<Zusatzpaket>("plan_addons", { columns: "key, entitlement_key, active, price_month_cents, price_year_cents", order: [["sort_order", "asc"]] })
    .then((r) => r.filter((a) => a.active)).catch(() => []);
}

/** Kleinster Tarif mit dem Modul als Katalogeintrag (aktive zuerst, dann Reihenfolge), sonst null. */
function katalogTarifMitModul(key: string, katalog?: TarifKatalog | null, creator = false): TarifKatalog["plans"][number] | null {
  if (!katalog?.plans.length) return null;
  // 0349: im Creator-Konto nur Creator-Tarife (Creator Pro), in Agenturen nur Agentur-Tarife (wie app.require_module)
  const mit = katalog.plans
    .filter((p) => isCreatorPlan(p.key) === creator)
    .filter((p) => katalog.entitlements.some((e) => e.plan_key === p.key && e.key === key && e.value > 0))
    .sort((a, b) => Number(b.active) - Number(a.active) || a.sort_order - b.sort_order || a.key.localeCompare(b.key));
  return mit[0] ?? null;
}

/**
 * Name des kleinsten Tarifs, der das Modul enthält. Aus dem Katalog wie app.require_module (aktive zuerst, dann Reihenfolge),
 * sonst aus der festen Zuordnung; unbekanntes Modul ohne Katalogeintrag: null.
 */
export function tarifMitModul(key: string, katalog?: TarifKatalog | null, creator = false): string | null {
  const k = katalogTarifMitModul(key, katalog, creator);
  if (k) return planName(k);
  if (creator) return null;
  const fest = MODUL_AB_TARIF[key as ModulKey];
  return fest ? TARIF_NAME[fest]?.() ?? null : null;
}

/** Schlüssel des kleinsten Tarifs mit dem Modul (Ziel des Knopfs „Upgrade auf …“, K-374); wie tarifMitModul. */
export function tarifKeyMitModul(key: string, katalog?: TarifKatalog | null, creator = false): string | null {
  const k = katalogTarifMitModul(key, katalog, creator);
  if (k) return k.key;
  if (creator) return null;
  return MODUL_AB_TARIF[key as ModulKey] ?? null;
}

/** „Im Tarif Team enthalten.“ */
export function imTarifText(tarif: string | null): string {
  return tarif ? t(`Im Tarif ${tarif} enthalten.`, `Included in the ${tarif} plan.`) : t("In einem größeren Tarif enthalten.", "Included in a larger plan.");
}

/** Einheitlicher Wortlaut an gesperrten Modulen (K-374), ohne Schlusspunkt: „Im Tarif Team enthalten“ */
export function imTarifKurz(tarif: string | null): string {
  return tarif ? t(`Im Tarif ${tarif} enthalten`, `Included in the ${tarif} plan`) : t("In einem größeren Tarif enthalten", "Included in a larger plan");
}

/** „Upgrade auf Team“ (Knopf an gesperrten Modulen, im Kontomenü und auf Abo & Tarif) */
export function upgradeText(tarif: string): string {
  return t(`Upgrade auf ${tarif}`, `Upgrade to ${tarif}`);
}

// ── Upgrade-Ziel (K-370, K-371, K-373, K-374) ───────────────────────────────────────────────────────

/** Buchbare Agentur-Tarife von klein nach groß (Preismodell 2026): Solo → Team → Agentur. */
export const TARIF_STUFEN = ["start", "team", "agentur"] as const;

/** Weg zum Wechseldialog mit vorgewähltem Tarif: Abo & Tarif öffnet ihn mit ?wechsel=<schlüssel>. */
export function wechselLink(planKey: string): string {
  return `/agentur/einstellungen/abo?wechsel=${encodeURIComponent(planKey)}`;
}

/**
 * Nächster größerer buchbarer Tarif: Solo → Team → Agentur. Im Tarif Agentur, im Creator-Konto, bei unbekannten oder
 * Alt-Tarifen (z. B. „agentur_alt“) und ohne Tarif: null. Mit Katalog nur, wenn der Zieltarif dort aktiv ist.
 */
export function naechsterTarif(planKey: string | null | undefined, opts: { creator?: boolean; plans?: readonly { key: string; active: boolean }[] | null } = {}): string | null {
  if (!planKey) return null;
  // 0349: Creator-Konto: Creator → Creator Pro
  if (opts.creator) return planKey === "creator" && (!opts.plans?.length || opts.plans.some((p) => p.key === "creator_pro" && p.active)) ? "creator_pro" : null;
  const i = (TARIF_STUFEN as readonly string[]).indexOf(planKey);
  if (i < 0) return null;
  for (const ziel of TARIF_STUFEN.slice(i + 1)) {
    if (!opts.plans?.length) return ziel;
    if (opts.plans.some((p) => p.key === ziel && p.active)) return ziel;
  }
  return null;
}

/**
 * Wert von ?wechsel= auf Abo & Tarif: ein aktiver Tarif des Katalogs, sonst null (Tippfehler, alter Link, fremder Wert).
 * Groß- und Kleinschreibung zählt nicht.
 */
export function wechselZiel(param: string | null | undefined, plans: readonly { key: string; active: boolean }[]): string | null {
  const v = (param ?? "").trim().toLowerCase();
  if (!v || !/^[a-z0-9_]{1,40}$/.test(v)) return null;
  return plans.some((p) => p.key === v && p.active) ? v : null;
}

/**
 * Meldung der Datenbank „tarif_modul:<schlüssel>“ als Satz in der gewünschten Sprache; der Tarif kommt aus dem Hinweis der
 * Datenbank, sonst aus der festen Zuordnung. Andere Meldungen: null.
 */
export function tarifModulMeldung(message: string, hint: string | null | undefined, lang: Lang): string | null {
  const m = /^tarif_modul:([a-z0-9_]+)/.exec(message.trim());
  if (!m) return null;
  const tarif = hint?.trim() || tarifMitModul(m[1]!);
  return tarif
    ? tr(lang, `Diese Funktion ist im Tarif ${tarif} enthalten.`, `This feature is included in the ${tarif} plan.`)
    : tr(lang, "Diese Funktion ist in einem größeren Tarif enthalten.", "This feature is included in a larger plan.");
}

// ── Grenzen ab 80 % Belegung ──────────────────────────────────────────────────────────────────────────

export interface Speichernutzung {
  bytes: number;
  limit_gb: number;
}
/** Belegter Speicher der Agentur gegen die Grenze des Tarifs (0042 org_storage_usage). */
export async function fetchSpeicher(api: Pick<Api, "read">, orgId: string): Promise<Speichernutzung> {
  const r = await api.read<Speichernutzung | string>("org_storage_usage", { p_org: orgId });
  return (typeof r === "string" ? JSON.parse(r) : r) as Speichernutzung;
}

export interface GrenzHinweis {
  key: "talents" | "seats" | "contracts" | "storage";
  used: number;
  max: number;
  /** Anteil 0…100 (gerundet, höchstens 100) */
  percent: number;
  /** Grenze erreicht oder überschritten */
  erreicht: boolean;
  text: string;
}
/** Ab diesem Anteil erscheint der Hinweis (80 %). */
export const GRENZ_SCHWELLE = 0.8;
const GB = 1024 ** 3;

function gbText(bytes: number): string {
  return new Intl.NumberFormat(numberLocale(), { maximumFractionDigits: bytes < GB ? 2 : 1 }).format(bytes / GB);
}

/**
 * Grenzen, die zu mindestens 80 % belegt sind: Creator, Team-Plätze, Verträge im Monat und Speicher. Unbegrenzt (-1),
 * fehlende Grenzen und Grenzen von 0 erscheinen nie. Text z. B. „4 von 5 Creator-Plätzen belegt“, bei 100 % mit
 * „Grenze erreicht“.
 * K-376: Die Team-Plätze zählen ohne den Platz des Inhabers. Ein Tarif mit genau einem Platz (Solo: nur der Inhaber) hat
 * damit keine Grenze zum Hinweisen; „1 von 1 Team-Plätzen“ war ein Dauerhinweis, obwohl nichts verhindert wurde.
 */
export function grenzHinweise(limits: Record<string, number>, usage: TarifNutzung | null, storage?: Speichernutzung | null): GrenzHinweis[] {
  const out: GrenzHinweis[] = [];
  /** `zaehlUsed`/`zaehlMax`: Zähler für Schwelle und „erreicht“ (bei Plätzen ohne den Inhaber), der Text nennt die vollen Zahlen */
  const add = (key: GrenzHinweis["key"], used: number, max: number | undefined, text: (used: number, max: number) => string, ohne = 0) => {
    if (max === undefined || max === null || !Number.isFinite(max) || max <= 0) return;
    const zaehlMax = max - ohne;
    const zaehlUsed = Math.max(0, used - ohne);
    if (zaehlMax <= 0) return;
    if (zaehlUsed / zaehlMax < GRENZ_SCHWELLE) return;
    const erreicht = zaehlUsed >= zaehlMax;
    const base = text(used, max);
    out.push({ key, used, max, percent: Math.min(100, Math.round((zaehlUsed / zaehlMax) * 100)), erreicht, text: erreicht ? `${base}${t(" · Grenze erreicht", " · limit reached")}` : base });
  };
  if (usage) {
    add("talents", usage.talents, limits.max_talents, (u, m) => t(`${u} von ${m} Creator-Plätzen belegt`, `${u} of ${m} creator slots in use`));
    add("seats", usage.seats, limits.max_seats, (u, m) => t(`${u} von ${m} Team-Plätzen belegt`, `${u} of ${m} team seats in use`), 1);
    add("contracts", usage.contracts_this_month, limits.max_contracts_per_month, (u, m) => t(`${u} von ${m} Verträgen in diesem Monat versendet`, `${u} of ${m} contracts sent this month`));
  }
  // Speicher: Grenze aus dem Speicherstand (dieselbe Quelle wie die Abo-Seite), sonst aus den Grenzen des Tarifs
  if (storage) {
    const limitGb = Number.isFinite(storage.limit_gb) ? storage.limit_gb : limits.storage_gb;
    if (limitGb !== undefined && limitGb > 0) {
      add("storage", storage.bytes, limitGb * GB, (u) => t(`${gbText(u)} von ${limitGb} GB Speicher belegt`, `${gbText(u)} of ${limitGb} GB storage in use`));
    }
  }
  return out;
}
